import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, api } from '../api';
import { ErrorBox, fmtDate, useAsync } from './ui';

const KIND: Record<string, string> = {
  note: 'Jegyzet', created: 'Létrehozva', updated: 'Módosítva', archived: 'Archiválva', restored: 'Visszaállítva',
  contact_linked: 'Kontakt hozzárendelve', email: 'E-mail', call: 'Hívás', agent_step: 'Ügynöki lépés',
  meeting: 'Megbeszélés', stage_changed: 'Lépésváltás',
  invoice_issued: 'Bizonylat kiállítva', invoice_paid: 'Számla kifizetve', invoice_cancelled: 'Számla sztornózva',
  quote_created: 'Ajánlat készült', quote_sent: 'Ajánlat elküldve', quote_accepted: 'Ajánlat elfogadva', quote_rejected: 'Ajánlat elutasítva', quote_expired: 'Ajánlat lejárt', deal_linked: 'Új deal', task_created: 'Feladat kiírva', task_done: 'Feladat kész', task_reopened: 'Feladat újranyitva',
};
const FIELD: Record<string, string> = {
  name: 'név', tax_number: 'adószám', registration_number: 'cégjegyzékszám', address: 'székhely', industry: 'iparág', size: 'méret',
  website: 'weboldal', tags: 'címkék', summary: 'összefoglaló', first_name: 'keresztnév', last_name: 'vezetéknév', email: 'e-mail',
  phone: 'telefon', position: 'pozíció', addressing: 'megszólítás', company_id: 'cég', consents: 'hozzájárulások',
  title: 'megnevezés', value: 'érték', currency: 'pénznem', probability: 'valószínűség', expected_close: 'várható zárás',
  owner_user_id: 'felelős', next_step: 'következő lépés', contacts: 'kontaktok',
};

/** Aktivitási idővonal: az ügynöki bejegyzések megkülönböztető jelölést kapnak (spec 5. fejezet). */
export function Timeline({ path, reloadKey = 0 }: { path: string; reloadKey?: number }) {
  const [items, setItems] = useState<Activity[] | null>(null);
  const [text, setText] = useState('');
  const a = useAsync();
  const load = () => a.run(async () => setItems((await api<{ items: Activity[] }>(`${path}/timeline`)).items));
  useEffect(() => { load(); }, [path, reloadKey]);

  const add = async () => {
    if (!text.trim()) return;
    const ok = await a.run(() => api(`${path}/notes`, { method: 'POST', body: { text } }));
    if (ok) { setText(''); load(); }
  };

  return (
    <section className="card">
      <h3>Idővonal</h3>
      <div className="note-add">
        <textarea placeholder="Jegyzet hozzáadása…" value={text} onChange={(e) => setText(e.target.value)} rows={2} />
        <button onClick={add} disabled={a.busy || !text.trim()}>Mentés</button>
      </div>
      <ErrorBox error={a.error} />
      <ol className="timeline">
        {items?.map((i) => (
          <li key={i.id} className={i.actor_kind === 'agent' ? 'agent' : ''}>
            <div className="tl-head">
              <strong>{KIND[i.kind] ?? i.kind}</strong>
              {i.actor_kind === 'agent' && <span className="badge badge-agent">ügynök</span>}
              {i.actor_kind === 'import' && <span className="badge badge-import">import</span>}
              <span className="muted">{fmtDate(i.occurred_at)}</span>
            </div>
            {i.kind === 'note' && <p className="pre">{i.body.text}</p>}
            {['call', 'email', 'meeting'].includes(i.kind) && (
              <>
                {(i.body.title || i.body.direction || i.body.duration_min) && <p className="muted">{[i.body.title, i.body.direction === 'in' ? 'bejövő' : i.body.direction === 'out' ? 'kimenő' : null, i.body.duration_min ? `${i.body.duration_min} perc` : null].filter(Boolean).join(' · ')}</p>}
                {i.body.text && <p className="pre">{i.body.text}</p>}
              </>
            )}
            {i.kind === 'stage_changed' && (
              <p>{i.body.from?.name} → <strong>{i.body.to?.name}</strong>{i.body.lost_reason && <span className="muted"> · ok: {i.body.lost_reason}</span>}</p>
            )}
            {i.kind.startsWith('quote_') && i.body.quote_id && <p><Link to={`/ajanlatok/${i.body.quote_id}`}>{i.body.label}</Link>{i.body.gross_total != null && <span className="muted"> · bruttó {new Intl.NumberFormat('hu-HU').format(i.body.gross_total)} {i.body.currency === 'EUR' ? '€' : 'Ft'}</span>}{i.body.note && <span className="muted"> · {i.body.note}</span>}</p>}
            {i.kind.startsWith('invoice_') && i.body.invoice_id && <p><Link to={`/szamlak/${i.body.invoice_id}`}>{i.body.number}</Link>
              <span className="muted"> · {i.body.kind === 'proforma' ? 'díjbekérő' : i.body.kind === 'cancellation' ? 'sztornó' : 'számla'} · bruttó {new Intl.NumberFormat('hu-HU').format(i.body.gross_total)} {i.body.currency === 'EUR' ? '€' : 'Ft'}</span></p>}
            {i.kind === 'deal_linked' && <p><Link to={`/ertekesites/${i.body.deal_id}`}>{i.body.title}</Link></p>}
            {['task_created', 'task_done', 'task_reopened'].includes(i.kind) && <p>{i.body.title}{i.body.due_date && <span className="muted"> · határidő: {i.body.due_date}</span>}</p>}
            {i.kind === 'updated' && i.body.diff && (
              <p className="muted">{Object.keys(i.body.diff).map((k) => FIELD[k] ?? k).join(', ')}</p>
            )}
          </li>
        ))}
        {items?.length === 0 && <li className="muted">Még nincs bejegyzés.</li>}
      </ol>
    </section>
  );
}
